import { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Space,
  Select,
  Input,
  Row,
  Col,
  Tag,
  Statistic,
  Modal,
  Button,
  message,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  TeamOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  CloudSyncOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useStudents } from "../../../hooks/finance/useStudents";
import { usePayments } from "../../../hooks/finance/usePayments";
import StudentsTable from "../../../components/finance/students/StudentsTable";
import StudentFinanceDrawer from "../../../components/finance/StudentFinanceDrawer";
import SurePayStudentMatchingModal from "../../../components/finance/payments/SurePayStudentMatchingModal";
import SurePayStudentCodeModal from "../../../components/finance/payments/SurePayStudentCodeModal";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Option } = Select;
const { confirm } = Modal;

/**
 * Students Management Page
 * Displays all students with filters, parent account status, and link management
 */
const StudentsManagement = () => {
  const navigate = useNavigate();
  const {
    students,
    users,
    loading,
    usersLoading,
    linkLoading,
    pagination,
    serverStats,
    fetchStudents,
    fetchUsers,
    linkStudent,
    unlinkStudent,
    updateStudentLink,
  } = useStudents();

  const {
    uploadSurePayStudentCSV,
    addSurePayStudentCode,
    removeSurePayStudentCode,
    searchSurePayStudents,
  } = usePayments();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);

  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [editLinkModalVisible, setEditLinkModalVisible] = useState(false);
  const [studentToLink, setStudentToLink] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  const isFirstMount = useRef(true);

  // Finance drawer state
  const [financeDrawerVisible, setFinanceDrawerVisible] = useState(false);
  const [selectedStudentForFinance, setSelectedStudentForFinance] =
    useState(null);
  const [surePaySyncVisible, setSurePaySyncVisible] = useState(false);
  const [surePayCodeVisible, setSurePayCodeVisible] = useState(false);
  const [surePayCodeMode, setSurePayCodeMode] = useState("add");
  const [selectedStudentForSurePay, setSelectedStudentForSurePay] =
    useState(null);

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
    admission_status: null,
    hasParentAccount: null,
    parentUserId: null,
    classId: null,
    streamId: null,
    dayBoarding: null,
    acadyr: null,
    feeStatus: null,
  });

  // Check if any filter is active
  const hasActiveFilters =
    filters.searchText ||
    filters.admission_status ||
    filters.hasParentAccount ||
    filters.parentUserId ||
    filters.classId ||
    filters.streamId ||
    filters.dayBoarding ||
    filters.acadyr ||
    filters.feeStatus;

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      searchText: "",
      admission_status: null,
      hasParentAccount: null,
      parentUserId: null,
      classId: null,
      streamId: null,
      dayBoarding: null,
      acadyr: null,
      feeStatus: null,
    });
  };

  // Export state
  const [exporting, setExporting] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    admitted: 0,
    day: 0,
    boarding: 0,
    feesCleared: 0,
    linked: 0,
  });

  /**
   * Fetch classes
   */
  const fetchClasses = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (response.ok) {
        const classesData = Array.isArray(response.data) ? response.data : [];
        setClasses(classesData);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    }
  }, [navigate]);

  /**
   * Fetch streams
   */
  const fetchStreams = useCallback(async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.stream,
        null,
        navigate,
      );
      if (response.ok) {
        const streamsData = Array.isArray(response.data) ? response.data : [];
        setStreams(streamsData);
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
      setStreams([]);
    }
  }, [navigate]);

  /**
   * Initial data load - exclude DRAFT status by default
   */
  useEffect(() => {
    fetchStudents({ filters: { exclude_status: "DRAFT" } });
    fetchUsers(null); // Fetch all users for filter dropdown
    fetchClasses();
    fetchStreams();
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!serverStats) return;
    setStats({
      total: serverStats.total ?? 0,
      admitted: serverStats.admitted ?? 0,
      day: serverStats.day ?? 0,
      boarding: serverStats.boarding ?? 0,
      feesCleared: serverStats.fees_cleared ?? 0,
      linked: serverStats.linked ?? 0,
    });
  }, [serverStats]);

  /**
   * Build query filters object
   */
  const buildQueryFilters = useCallback(() => {
    const queryFilters = { exclude_status: "DRAFT" };
    if (filters.admission_status) {
      queryFilters.status = filters.admission_status;
      delete queryFilters.exclude_status;
    }
    if (filters.searchText) {
      queryFilters.search = filters.searchText;
    }
    if (filters.parentUserId) {
      queryFilters.user = filters.parentUserId;
    }
    if (filters.classId) {
      const selectedClass = classes.find((c) => c.id === filters.classId);
      if (selectedClass) queryFilters.className = selectedClass.name;
    }
    if (filters.streamId) {
      const selectedStream = streams.find((s) => s.id === filters.streamId);
      if (selectedStream) queryFilters.streamName = selectedStream.name;
    }
    if (filters.dayBoarding) {
      queryFilters.day_boarding = filters.dayBoarding;
    }
    if (filters.acadyr) {
      queryFilters.acadyr = filters.acadyr;
    }
    if (filters.hasParentAccount) {
      queryFilters.has_parent_account = filters.hasParentAccount;
    }
    if (filters.feeStatus) {
      queryFilters.fee_status = filters.feeStatus;
    }
    return queryFilters;
  }, [filters, classes, streams]);

  /**
   * Handle server-side filter changes
   * Debounced to avoid spam requests
   */
  useEffect(() => {
    // Skip initial mount - initial fetch is done in the first useEffect
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      const queryFilters = buildQueryFilters();
      fetchStudents({ filters: queryFilters, page: 1 });
    }, 300);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.admission_status,
    filters.parentUserId,
    filters.classId,
    filters.streamId,
    filters.dayBoarding,
    filters.acadyr,
    filters.searchText,
  ]);

  /**
   * Handle view fees - open finance drawer on the same page
   */
  const handleViewFees = (student) => {
    const spc = student.spcCode || student.spc;
    const firstName = student.firstName || student.first_name || "";
    const lastName = student.lastName || student.last_name || "";
    setSelectedStudentForFinance({
      spc,
      spcCode: spc,
      firstName,
      first_name: firstName,
      lastName,
      last_name: lastName,
    });
    setFinanceDrawerVisible(true);
  };

  /**
   * Close finance drawer
   */
  const handleCloseFinanceDrawer = () => {
    setFinanceDrawerVisible(false);
    setSelectedStudentForFinance(null);
    fetchStudents({
      filters: buildQueryFilters(),
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleAddSurePayCode = (student) => {
    setSelectedStudentForSurePay(student);
    setSurePayCodeMode("add");
    setSurePayCodeVisible(true);
  };

  const handleChangeSurePayCode = (student) => {
    setSelectedStudentForSurePay(student);
    setSurePayCodeMode("change");
    setSurePayCodeVisible(true);
  };

  const handleRemoveSurePayCode = (student) => {
    const fullName =
      `${student.lastName || ""} ${student.firstName || ""} ${student.middleName || ""}`.trim();
    confirm({
      title: "Remove SurePay code?",
      content: `Remove ${student.surePayStudentCode} from ${fullName}? The code will become available to assign to the correct student.`,
      okText: "Remove code",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const result = await removeSurePayStudentCode(student.id);
        if (result) {
          fetchStudents({
            filters: buildQueryFilters(),
            page: pagination.current,
            pageSize: pagination.pageSize,
          });
        }
      },
    });
  };

  const handleSurePayCodeSaved = () => {
    setSurePayCodeVisible(false);
    setSelectedStudentForSurePay(null);
    fetchStudents({
      filters: buildQueryFilters(),
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const handleCloseSurePayCodeModal = () => {
    setSurePayCodeVisible(false);
    setSelectedStudentForSurePay(null);
  };

  /**
   * Handle link from table
   */
  const handleLinkFromTable = (student) => {
    setStudentToLink(student);
    setLinkModalVisible(true);
    fetchUsers(null); // Fetch all users (staff and non-staff)
  };

  /**
   * Handle edit link from table
   */
  const handleEditLinkFromTable = (student) => {
    setStudentToLink(student);
    setSelectedUserId(student.parentAccount?.userID || null);
    setEditLinkModalVisible(true);
    fetchUsers(null); // Fetch all users (staff and non-staff)
  };

  /**
   * Handle unlink from table
   */
  const handleUnlinkFromTable = (student) => {
    const fullName =
      `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim();
    confirm({
      title: "Unlink Parent Account",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to unlink the parent account from ${fullName}?`,
      okText: "Yes, Unlink",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        unlinkStudent(student.id);
      },
    });
  };

  /**
   * Handle link confirmation
   */
  const handleLinkConfirm = async () => {
    if (!studentToLink || !selectedUserId) return;
    await linkStudent(studentToLink.id, selectedUserId);
    setLinkModalVisible(false);
    setStudentToLink(null);
    setSelectedUserId(null);
  };

  /**
   * Handle edit link confirmation
   */
  const handleEditLinkConfirm = async () => {
    if (!studentToLink || !selectedUserId) return;
    await updateStudentLink(studentToLink.id, selectedUserId);
    setEditLinkModalVisible(false);
    setStudentToLink(null);
    setSelectedUserId(null);
  };

  /**
   * Handle pagination change
   */
  const handlePaginationChange = (page, pageSize) => {
    const queryFilters = buildQueryFilters();
    fetchStudents({
      page,
      pageSize,
      filters: queryFilters,
    });
  };

  /**
   * Export students data to Excel with financial information.
   * Fetches ALL records matching current filters (not just the current page).
   */
  const handleExportToExcel = async () => {
    if (pagination.total === 0) {
      message.warning("No data to export");
      return;
    }

    setExporting(true);
    message.loading({
      content: "Fetching all student records...",
      key: "export",
      duration: 0,
    });

    try {
      // Fetch all matching students without pagination limit
      const queryFilters = buildQueryFilters();
      const queryObj = { ...queryFilters, pageSize: 9999 };
      const queryParams = new URLSearchParams(queryObj).toString();
      const allUrl = `${domainSettings.urls.enrollment.students}${queryParams ? "?" + queryParams : ""}`;
      const allResponse = await Utilities.fetchWithAuth(
        "GET",
        allUrl,
        null,
        navigate,
      );

      if (!allResponse.ok) {
        message.error({
          content: "Failed to fetch students for export",
          key: "export",
        });
        return;
      }

      const allStudents = Array.isArray(allResponse.data)
        ? allResponse.data
        : allResponse.data?.results || [];

      if (allStudents.length === 0) {
        message.warning({
          content: "No matching students to export",
          key: "export",
        });
        return;
      }

      message.loading({
        content: `Preparing export for ${allStudents.length} student(s)...`,
        key: "export",
        duration: 0,
      });

      const exportData = [];

      for (const student of allStudents) {
        const spc = student.spcCode || student.spc;
        const fullName =
          `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`.trim();

        let financialData = {
          totalDue: 0,
          totalPaid: 0,
          balance: 0,
          schoolFeesBalance: 0,
          customFeesBalance: 0,
          transportFeesBalance: 0,
          botFeesBalance: 0,
        };

        // Fetch complete financial data in ONE API call
        if (spc) {
          try {
            const response = await Utilities.fetchWithAuth(
              "GET",
              domainSettings.urls.finance.studentCompleteFinances.replace(
                ":spc",
                spc,
              ),
              null,
              navigate,
            );

            if (response.ok && response.data) {
              const data = response.data;
              financialData = {
                totalDue: parseFloat(data.grandTotal.totalDue) || 0,
                totalPaid: parseFloat(data.grandTotal.totalPaid) || 0,
                balance: parseFloat(data.grandTotal.balance) || 0,
                schoolFeesBalance: parseFloat(data.schoolFees.balance) || 0,
                customFeesBalance: parseFloat(data.customFees.balance) || 0,
                transportFeesBalance:
                  parseFloat(data.transportFees.balance) || 0,
                botFeesBalance: parseFloat(data.botFees.balance) || 0,
              };
            }
          } catch (err) {
            console.error(`Error fetching finances for ${spc}:`, err);
          }
        }

        exportData.push({
          "Student Name": fullName,
          "SPC Code": student.spcCode || "-",
          Class: student.className || "-",
          Stream: student.streamName || "-",
          "Day/Boarding": student.dayBoarding || "-",
          "Academic Year": student.current_acadyr || "-",
          "Admission Status": student.admission_status || "-",
          "Link Status": student.hasParentAccount ? "Linked" : "Not Linked",
          "Parent Account":
            student.parentAccount?.fullName ||
            student.parentAccount?.email ||
            "-",
          "Total Fees Due": financialData.totalDue,
          "Total Paid": financialData.totalPaid,
          "Total Balance": financialData.balance,
          "School Fees Balance": financialData.schoolFeesBalance,
          "Custom Fees Balance": financialData.customFeesBalance,
          "Transport Fees Balance": financialData.transportFeesBalance,
          "BOT Fees Balance": financialData.botFeesBalance,
        });
      }

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Students");

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      ws["!cols"] = colWidths;

      // Generate filename with timestamp
      const fileName = `students_finance_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success({
        content: `Exported ${exportData.length} student(s)`,
        key: "export",
      });
    } catch (error) {
      console.error("Export error:", error);
      message.error({ content: "Failed to export data", key: "export" });
    } finally {
      setExporting(false);
    }
  };

  // All filters are now server-side; students already reflect the active filter set.
  const filteredStudents = students;

  return (
    <>
      <Helmet>
        <title>Students | Finance</title>
      </Helmet>

      <div className="content-wrapper">
        <div className="mg-b-20">
          <h4 className="mg-b-5">Students Management</h4>
          <p className="tx-color-03 mg-b-0">
            View all students, manage account links, and access financial data.
          </p>
        </div>

        {/* Summary Cards — Row 1 of 2 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Total Students"
                value={stats.total}
                prefix={<TeamOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Admitted"
                value={stats.admitted}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Linked to Parent"
                value={stats.linked}
                suffix={`/ ${stats.total}`}
                prefix={<LinkOutlined />}
                valueStyle={{
                  color:
                    stats.linked === stats.total
                      ? "#52c41a"
                      : stats.linked === 0
                        ? "#f5222d"
                        : "#fa8c16",
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Summary Cards — Row 2 of 2 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Day Scholars"
                value={stats.day}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Boarders"
                value={stats.boarding}
                prefix={<HomeOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Fees Cleared"
                value={stats.feesCleared}
                suffix={`/ ${stats.total}`}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          {/* Search Bar */}
          <Input
            placeholder="Search by name, SPC, SurePay code, or parent email..."
            prefix={<SearchOutlined />}
            value={filters.searchText}
            onChange={(e) =>
              setFilters({ ...filters, searchText: e.target.value })
            }
            allowClear
            size="large"
            style={{ marginBottom: 16 }}
          />

          {/* Filters Section */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <FilterOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                <span style={{ fontWeight: 500, marginRight: 16 }}>
                  Filters
                </span>
                {hasActiveFilters && (
                  <Button
                    type="link"
                    size="small"
                    icon={<ClearOutlined />}
                    onClick={clearAllFilters}
                    style={{ padding: 0 }}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportToExcel}
                  loading={exporting}
                  disabled={pagination.total === 0}
                >
                  Export to Excel
                </Button>
                <Button
                  icon={<CloudSyncOutlined />}
                  onClick={() => setSurePaySyncVisible(true)}
                >
                  Sync SurePay
                </Button>
              </Space>
            </div>

            <Row gutter={[12, 12]}>
              {/* Academic Filters */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.classId}
                  onChange={(value) =>
                    setFilters({ ...filters, classId: value ?? null })
                  }
                >
                  {Array.isArray(classes) &&
                    classes.map((cls) => (
                      <Option key={cls.id} value={cls.id}>
                        {cls.name}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Stream"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.streamId}
                  onChange={(value) =>
                    setFilters({ ...filters, streamId: value ?? null })
                  }
                >
                  {Array.isArray(streams) &&
                    streams.map((stream) => (
                      <Option key={stream.id} value={stream.id}>
                        {stream.name}
                      </Option>
                    ))}
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Day/Boarding"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.dayBoarding}
                  onChange={(value) =>
                    setFilters({ ...filters, dayBoarding: value ?? null })
                  }
                >
                  <Option value="Day">Day</Option>
                  <Option value="Boarding">Boarding</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Calendar Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyr}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyr: value ?? null })
                  }
                >
                  {Array.isArray(academicYears) &&
                    academicYears.map((year) => {
                      // Extract calendar year (second part of acadyr like "2025/2026" -> "2026")
                      const calendarYear = year.acadyr?.includes("/")
                        ? year.acadyr.split("/")[1]
                        : year.acadyr;
                      return (
                        <Option key={year.id} value={year.acadyr}>
                          {calendarYear}
                        </Option>
                      );
                    })}
                </Select>
              </Col>
              {/* Status Filters */}
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Admission"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.admission_status}
                  onChange={(value) =>
                    setFilters({ ...filters, admission_status: value ?? null })
                  }
                >
                  <Option value="SUBMITTED">Submitted</Option>
                  <Option value="INTERVIEW">Interview</Option>
                  <Option value="VERIFIED">Verified</Option>
                  <Option value="APPROVED">Approved</Option>
                  <Option value="ADMITTED">Admitted</Option>
                  <Option value="REJECTED">Rejected</Option>
                  <Option value="WAITING LIST">Waiting List</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Link Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.hasParentAccount}
                  onChange={(value) =>
                    setFilters({ ...filters, hasParentAccount: value ?? null })
                  }
                >
                  <Option value="linked">Linked</Option>
                  <Option value="not_linked">Not Linked</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Fee Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.feeStatus}
                  onChange={(value) =>
                    setFilters({ ...filters, feeStatus: value ?? null })
                  }
                >
                  <Option value="cleared">Cleared</Option>
                  <Option value="pending">Pending</Option>
                </Select>
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={3}>
                <Select
                  placeholder="Parent"
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  loading={usersLoading}
                  value={filters.parentUserId}
                  onChange={(value) =>
                    setFilters({ ...filters, parentUserId: value ?? null })
                  }
                  filterOption={(input, option) => {
                    const user = users.find((u) => u.userID === option.value);
                    if (!user) return false;
                    const searchText = input.toLowerCase();
                    const fullName = (user.fullName || "").toLowerCase();
                    const email = (user.email || "").toLowerCase();
                    return (
                      fullName.includes(searchText) ||
                      email.includes(searchText)
                    );
                  }}
                >
                  {users.map((user) => (
                    <Option key={user.userID} value={user.userID}>
                      {user.fullName || user.email}{" "}
                      {user.staffId ? `(Staff)` : ""}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </div>

          {/* Results Summary */}
          {hasActiveFilters && (
            <div style={{ marginBottom: 16 }}>
              <Tag color="blue">Showing {pagination.total} student(s)</Tag>
            </div>
          )}

          {/* Students Table */}
          <StudentsTable
            data={filteredStudents}
            loading={loading}
            onViewFees={handleViewFees}
            onLink={handleLinkFromTable}
            onUnlink={handleUnlinkFromTable}
            onEditLink={handleEditLinkFromTable}
            onAddSurePayCode={handleAddSurePayCode}
            onChangeSurePayCode={handleChangeSurePayCode}
            onRemoveSurePayCode={handleRemoveSurePayCode}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
          />
        </Card>

        {/* Link Parent Account Modal */}
        <Modal
          title="Link Parent Account"
          open={linkModalVisible}
          onOk={handleLinkConfirm}
          onCancel={() => {
            setLinkModalVisible(false);
            setStudentToLink(null);
            setSelectedUserId(null);
          }}
          okText="Link Account"
          okButtonProps={{ disabled: !selectedUserId, loading: linkLoading }}
        >
          {studentToLink && (
            <div style={{ marginBottom: 16 }}>
              <p>
                Link a parent account to{" "}
                <strong>
                  {`${studentToLink.firstName || ""} ${studentToLink.lastName || ""}`.trim()}
                </strong>
              </p>
            </div>
          )}
          <Select
            placeholder="Search and select a user account..."
            style={{ width: "100%" }}
            loading={usersLoading}
            showSearch
            value={selectedUserId}
            onChange={setSelectedUserId}
            filterOption={(input, option) => {
              const user = users.find((u) => u.userID === option.value);
              if (!user) return false;
              const searchText = input.toLowerCase();
              const fullName = (user.fullName || "").toLowerCase();
              const email = (user.email || "").toLowerCase();
              return (
                fullName.includes(searchText) || email.includes(searchText)
              );
            }}
          >
            {users.map((user) => (
              <Option key={user.userID} value={user.userID}>
                {user.fullName || user.email} - {user.email}{" "}
                {user.staffId ? "(Staff)" : ""}
              </Option>
            ))}
          </Select>
        </Modal>

        {/* Change Parent Account Modal */}
        <Modal
          title="Change Parent Account"
          open={editLinkModalVisible}
          onOk={handleEditLinkConfirm}
          onCancel={() => {
            setEditLinkModalVisible(false);
            setStudentToLink(null);
            setSelectedUserId(null);
          }}
          okText="Update Account"
          okButtonProps={{ disabled: !selectedUserId, loading: linkLoading }}
        >
          {studentToLink && (
            <div style={{ marginBottom: 16 }}>
              <p>
                Change parent account for{" "}
                <strong>
                  {`${studentToLink.firstName || ""} ${studentToLink.lastName || ""}`.trim()}
                </strong>
              </p>
              {studentToLink.parentAccount && (
                <p style={{ color: "#888" }}>
                  Current:{" "}
                  {studentToLink.parentAccount.fullName ||
                    studentToLink.parentAccount.email}
                </p>
              )}
            </div>
          )}
          <Select
            placeholder="Search and select a new user account..."
            style={{ width: "100%" }}
            loading={usersLoading}
            showSearch
            value={selectedUserId}
            onChange={setSelectedUserId}
            filterOption={(input, option) => {
              const user = users.find((u) => u.userID === option.value);
              if (!user) return false;
              const searchText = input.toLowerCase();
              const fullName = (user.fullName || "").toLowerCase();
              const email = (user.email || "").toLowerCase();
              return (
                fullName.includes(searchText) || email.includes(searchText)
              );
            }}
          >
            {users.map((user) => (
              <Option key={user.userID} value={user.userID}>
                {user.fullName || user.email} - {user.email}{" "}
                {user.staffId ? "(Staff)" : ""}
              </Option>
            ))}
          </Select>
        </Modal>

        {/* Student Finance Drawer */}
        <StudentFinanceDrawer
          visible={financeDrawerVisible}
          onClose={handleCloseFinanceDrawer}
          studentSpc={
            selectedStudentForFinance?.spc || selectedStudentForFinance?.spcCode
          }
          studentName={
            selectedStudentForFinance
              ? `${selectedStudentForFinance.firstName || selectedStudentForFinance.first_name} ${selectedStudentForFinance.lastName || selectedStudentForFinance.last_name}`
              : ""
          }
        />

        <SurePayStudentMatchingModal
          visible={surePaySyncVisible}
          onCancel={() => setSurePaySyncVisible(false)}
          uploadCSV={uploadSurePayStudentCSV}
        />

        <SurePayStudentCodeModal
          visible={surePayCodeVisible}
          onCancel={handleCloseSurePayCodeModal}
          onSaved={handleSurePayCodeSaved}
          searchStudents={searchSurePayStudents}
          addCode={addSurePayStudentCode}
          student={selectedStudentForSurePay}
          mode={surePayCodeMode}
        />
      </div>
    </>
  );
};

export default StudentsManagement;
